import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateTime, formatDate } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, CheckCircle, XCircle, Eye, MousePointer, Clock, RefreshCw, Loader2 } from 'lucide-react';

export default function AdminEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [logsRes, statsRes] = await Promise.all([
        axios.get('/api/admin/email-logs', {
          params: statusFilter !== 'all' ? { status: statusFilter } : {},
        }),
        axios.get('/api/admin/email-logs/stats'),
      ]);
      setLogs(logsRes.data.logs || []);
      setStats(statsRes.data.stats);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (log) => {
    const statusConfig = {
      pending: { variant: 'secondary', label: 'Pending' },
      sent: { variant: 'default', label: 'Sent' },
      delivered: { variant: 'default', label: 'Delivered', className: 'bg-green-600' },
      failed: { variant: 'destructive', label: 'Failed' },
      bounced: { variant: 'destructive', label: 'Bounced' },
      complained: { variant: 'destructive', label: 'Complained' },
    };
    const config = statusConfig[log.status] || { variant: 'secondary', label: log.status };
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const handleViewDetail = (log) => {
    setSelectedLog(log);
    setShowDetail(true);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="admin-email-logs-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-email-logs-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            Email Logs & Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track email delivery, opens, and clicks
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Sent</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">{stats.sent}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Delivered</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Opened</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-purple-600">{stats.opened}</p>
              <p className="text-xs text-muted-foreground">{stats.openRate}% rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Clicked</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-600">{stats.clicked}</p>
              <p className="text-xs text-muted-foreground">{stats.clickRate}% rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Queued</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-600">{stats.queued}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{logs.length} email(s)</span>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border rounded-[var(--radius)]">
          <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No email logs found</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[var(--radius)] overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium">Recipient</th>
                <th className="text-left p-4 text-sm font-medium">Subject</th>
                <th className="text-left p-4 text-sm font-medium">Template</th>
                <th className="text-left p-4 text-sm font-medium">Status</th>
                <th className="text-left p-4 text-sm font-medium">Tracking</th>
                <th className="text-left p-4 text-sm font-medium">Sent At</th>
                <th className="text-left p-4 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-t border-border hover:bg-muted/30">
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-sm">{log.userName || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{log.toEmail}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-sm truncate max-w-[200px]" title={log.subject}>
                      {log.subject}
                    </p>
                  </td>
                  <td className="p-4">
                    {log.templateSlug ? (
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {log.templateSlug}
                      </code>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="p-4">{getStatusBadge(log)}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {log.opened && (
                        <span className="flex items-center gap-1 text-xs text-purple-600">
                          <Eye className="w-3 h-3" /> Opened
                        </span>
                      )}
                      {log.clicked && (
                        <span className="flex items-center gap-1 text-xs text-amber-600">
                          <MousePointer className="w-3 h-3" /> Clicked
                        </span>
                      )}
                      {!log.opened && !log.clicked && (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-muted-foreground">
                      {formatDateTime(log.sentAt || log.createdAt)}
                    </span>
                  </td>
                  <td className="p-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDetail(log)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
            <DialogDescription>{selectedLog?.subject}</DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Recipient</p>
                  <p className="text-sm">{selectedLog.toEmail}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  {getStatusBadge(selectedLog)}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Template</p>
                  <p className="text-sm">
                    {selectedLog.templateSlug || 'Direct send'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Trigger Event</p>
                  <p className="text-sm">{selectedLog.triggerEvent || '-'}</p>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium text-muted-foreground mb-2">Timeline</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>Created: {formatDateTime(selectedLog.createdAt)}</span>
                  </div>
                  {selectedLog.sentAt && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="w-4 h-4 text-blue-600" />
                      <span>Sent: {formatDateTime(selectedLog.sentAt)}</span>
                    </div>
                  )}
                  {selectedLog.deliveredAt && (
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>Delivered: {formatDateTime(selectedLog.deliveredAt)}</span>
                    </div>
                  )}
                  {selectedLog.openedAt && (
                    <div className="flex items-center gap-3 text-sm">
                      <Eye className="w-4 h-4 text-purple-600" />
                      <span>Opened: {formatDateTime(selectedLog.openedAt)}</span>
                    </div>
                  )}
                  {selectedLog.clickedAt && (
                    <div className="flex items-center gap-3 text-sm">
                      <MousePointer className="w-4 h-4 text-amber-600" />
                      <span>Clicked: {formatDateTime(selectedLog.clickedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedLog.clickedLinks?.length > 0 && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Clicked Links</p>
                  <div className="space-y-1">
                    {selectedLog.clickedLinks.map((link, i) => (
                      <div key={i} className="text-sm text-muted-foreground">
                        {link.url} - {formatDate(link.clickedAt)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLog.error && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-medium text-destructive mb-2">Error</p>
                  <p className="text-sm text-muted-foreground bg-destructive/10 p-3 rounded">
                    {selectedLog.error}
                  </p>
                </div>
              )}

              {selectedLog.resendId && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-medium text-muted-foreground">Resend ID</p>
                  <code className="text-xs bg-muted px-2 py-1 rounded">
                    {selectedLog.resendId}
                  </code>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
